import { Radio, Form, FormField, Popup, Icon, Button, Message } from "grabcad-ui-elements";
import { ChangeEvent, FormEvent, useContext, useEffect, useRef, useState } from "react";
import { ApplicationContext } from "../../../components/ApplicationProvider";
import {
    ENABLE_OR_DISABLE_SSO,
    REGISTER_IDENTITY_PROVIDER,
} from "../../../graphql/Mutations/Configure_auth/sso_registerIdentityProvider";
import { useMutation } from "@apollo/client";
import { Notifier } from "../../../view/Notifier";
import {
    SSOWrapper,
    HeadingContainer,
    Row,
    InlineTable,
    InlineTableCell,
    ColumnB,
    ColumnA,
    RadioGroupContainer,
    ErrorMessage,
    InputContainer,
    RadioContainer,
    FileInput,
    FileLabel,
    StyledLabel,
    StyledLabelWrapper,
    StyledSup,
    StyledInfoIcon,
    SSOContainer,
    VersionInfoContainer,
    VersionText,
} from "./SsoStyledComponents";
import One from "../../../images/cognito_auth/one.svg";
import Two from "../../../images/cognito_auth/two.svg";
import Three from "../../../images/cognito_auth/three.svg";
import { CheckboxProps } from "semantic-ui-react";
import { base64ToFile, fileToBase64, IConfigureAuthProps } from "./ConfigAuthHelper";
import { AuthSettings } from "@grabcad/company-server-shared/dist/types";
import { UploadMetadataInfoModal } from "./UploadMetadataInfoModal";
import GrabcadDetails from "./GrabcadDetails";
import { SsoConfirmationModal } from "./SsoConfirmationModal";
import Parser from "html-react-parser";
import { UserContext } from "../../../components/User/UserProvider";
import { Role } from "../../../graphql";

export enum MetadataChoice {
    MetadataUrl = "metadataUrl",
    MetadataCertificate = "metadataCertificate",
}
interface MetadataCertificateFile {
    file: File | null;
    name: string | null;
}

interface FormState {
    samlAttribute: string;
    metadataUrl: string;
    metadataCertificate: MetadataCertificateFile;
    metadataSelection: MetadataChoice;
}

interface formErrors {
    samlAttribute?: string;
    metadataUrl?: string;
    file?: string;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
const ConfigureSSO = (props: IConfigureAuthProps) => {
    const { t } = useContext(ApplicationContext); // Localization context
    const userContext = useContext(UserContext);
    const isReadOnlyCompanyUser = userContext.role === Role.User;
    const initialFormState: FormState = {
        samlAttribute: "",
        metadataUrl: "",
        metadataCertificate: {
            file: null,
            name: null,
        },
        metadataSelection: MetadataChoice.MetadataUrl,
    };
    const [formState, setFormState] = useState<FormState>(initialFormState);
    const [savedState, setSavedState] = useState<FormState>(initialFormState);
    const [errors, setErrors] = useState<formErrors>({});
    const [isEditable, setIsEditable] = useState<boolean>(false);
    const [isDirty, setIsDirty] = useState<boolean>(false);
    const [isFormValid, setIsFormValid] = useState<boolean>(false);
    const [submitDisabled, setSubmitDisabled] = useState<boolean>(false);
    const [showModal, setShowModal] = useState(false);
    const [pendingMetadataChoice, setPendingMetadataChoice] = useState<MetadataChoice | null>(null);

    const [showConfirmation, setShowConfirmation] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);

    // Read-only users always see the static default option, never the actual configured selection
    const displayedMetadataSelection = isReadOnlyCompanyUser
        ? MetadataChoice.MetadataUrl
        : formState.metadataSelection;

    const [mutateToggle] = useMutation(ENABLE_OR_DISABLE_SSO, {
        onCompleted: () => {
            Notifier.success(
                t("configure_sso_setting.sso_toggle_sucess_msg", {
                    status: !isEditable ? "enabled" : "disabled",
                })
            );
        },
        onError: () => {
            Notifier.fail(t("configure_sso_setting.sso_toggle_error_msg"));
        },
    });

    const [mutate, { loading }] = useMutation(REGISTER_IDENTITY_PROVIDER, {
        onCompleted: (data: { registerIdentityProvider: AuthSettings }) => {
            let metadataCertificateFile = null;
            if (
                !!data?.registerIdentityProvider?.sso?.federatedIDPs[0]?.providerDetails
                    ?.metadataCertificate
            ) {
                metadataCertificateFile =
                    base64ToFile(
                        data?.registerIdentityProvider?.sso?.federatedIDPs[0]?.providerDetails
                            ?.metadataCertificate
                    ) || null;
            }

            setFormState({
                samlAttribute:
                    data?.registerIdentityProvider?.sso?.federatedIDPs[0]?.attributeMappings?.email,
                metadataUrl:
                    data?.registerIdentityProvider?.sso?.federatedIDPs[0]?.providerDetails
                        ?.metadataDocumentEndpointURL,
                metadataSelection: data?.registerIdentityProvider?.sso?.federatedIDPs[0]
                    ?.providerDetails?.metadataSelection as MetadataChoice,
                metadataCertificate: {
                    file: null,
                    name: metadataCertificateFile ? metadataCertificateFile.name : null,
                },
            });
            Notifier.success(t("configure_sso_setting.sso_sucess_msg"));
        },
        onError: () => {
            Notifier.fail(t("configure_sso_setting.sso_error_msg"));
        },
    });

    useEffect(() => {
        const ssoValue = { ...props?.authSettings?.sso };
        const firstFederatedIDP = ssoValue.federatedIDPs?.[0];

        let metadataCertificateFile = null;
        if (!!firstFederatedIDP?.providerDetails?.metadataCertificate) {
            metadataCertificateFile =
                base64ToFile(firstFederatedIDP?.providerDetails?.metadataCertificate) || null;
        }

        const formValue = {
            samlAttribute: firstFederatedIDP?.attributeMappings?.email || "",
            metadataUrl: firstFederatedIDP?.providerDetails?.metadataDocumentEndpointURL || "",
            metadataCertificate: {
                file: null,
                name: metadataCertificateFile ? metadataCertificateFile.name : null,
            },
            metadataSelection:
                (firstFederatedIDP?.providerDetails?.metadataSelection as MetadataChoice) ||
                MetadataChoice.MetadataUrl,
        };

        setFormState(formValue);
        setSavedState(formValue);

        setIsEditable(ssoValue?.enabled || false);
        setSubmitDisabled(!ssoValue?.enabled || true);
    }, [props]);

    useEffect(() => {
        const dirty =
            formState.metadataUrl !== savedState.metadataUrl ||
            formState.samlAttribute !== savedState.samlAttribute ||
            (formState.metadataCertificate.file !== null
                ? formState.metadataCertificate.file !== savedState.metadataCertificate.file
                : false) ||
            formState.metadataSelection !== savedState.metadataSelection;
        setIsDirty(dirty);
    }, [formState, savedState]);

    useEffect(() => {
        const isFileValid =
            formState.metadataCertificate.file !== null &&
            formState.metadataCertificate.name !== null;
        const isUrlvalid = formState.samlAttribute !== "";

        const formValid =
            isEditable &&
            formState.samlAttribute !== "" &&
            (formState.metadataSelection === MetadataChoice.MetadataUrl
                ? isUrlvalid
                : isFileValid) &&
            !errors.samlAttribute &&
            !errors.metadataUrl &&
            !errors.file;
        setIsFormValid(formValid);
    }, [errors, formState, isEditable]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>, field: string) => {
        if (isReadOnlyCompanyUser) {
            return;
        }

        const { value } = e.target;
        if (field === "samlAttribute") {
            setFormState((prevFormState) => ({ ...prevFormState, samlAttribute: value }));
        }
        if (field === "metadataUrl") {
            setFormState((prevFormState) => ({ ...prevFormState, metadataUrl: value }));
        }
        setErrors((prev) => ({ ...prev, [field]: undefined }));
    };

    const handleToggleChange = async () => {
        if (isReadOnlyCompanyUser) {
            return;
        }

        setIsEditable((prev) => !prev);
        try {
            await mutateToggle({ variables: { enableSSO: !isEditable } });
        } catch (error) {
            Notifier.fail(t("configure_sso_setting.sso_toggle_error_msg"));
        }
    };

    const handleMetadataSelection = (e: FormEvent<HTMLInputElement>, data: CheckboxProps) => {
        if (isReadOnlyCompanyUser) {
            return;
        }

        const selectedValue = data.value as MetadataChoice;

        if (selectedValue === MetadataChoice.MetadataCertificate) {
            // Defer update until modal confirmation
            setPendingMetadataChoice(selectedValue);
            setShowModal(true);
        } else {
            // Directly update form state for MetadataUrl
            setFormState((prevFormState) => ({
                ...prevFormState,
                metadataSelection: selectedValue,
                metadataCertificate: {
                    ...prevFormState.metadataCertificate,
                    file: null,
                },
            }));

            setErrors({});
        }
    };

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (isReadOnlyCompanyUser) {
            return;
        }

        const fileInput = e.target.files?.[0];
        if (fileInput) {
            // Validate file format
            if (fileInput.type !== "application/xml" && fileInput.type !== "text/xml") {
                setErrors((prev) => ({
                    ...prev,
                    file: t("configure_sso_setting.sso_config.metadata_invalid_certificate"),
                }));
                setFormState((prevFormState) => ({
                    ...prevFormState,
                    metadataCertificate: { ...prevFormState.metadataCertificate, file: null },
                })); // Reset file input on invalid format
                setSubmitDisabled(true);
            } else {
                setFormState((prevFormState) => ({
                    ...prevFormState,
                    metadataCertificate: { file: fileInput, name: fileInput.name },
                }));
                setErrors((prev) => ({ ...prev, file: undefined })); // Clear file error
                setSubmitDisabled(false);
            }
        }
    };

    const validateForm = () => {
        const newErrors: any = {};
        const urlPattern = /^(https?:\/\/)?([a-z0-9\-]+\.)+[a-z]{2,}(\/[^\s]*)?$/i;

        // Validate saml attribute
        if (!formState.samlAttribute) {
            newErrors.samlAttribute = t("configure_sso_setting.sso_config.saml_attribute_required");
        } else if (!/^(https?:\/\/[^\s]+|[a-zA-Z0-9\s]{1,200})$/.test(formState.samlAttribute)) {
            newErrors.samlAttribute = t(
                "configure_sso_setting.sso_config.saml_invalid_attribute_mapping"
            );
        }

        // Validate metadata URL if the selection is 'MetadataUrl'
        if (formState.metadataSelection === MetadataChoice.MetadataUrl) {
            if (!formState.metadataUrl) {
                newErrors.metadataUrl = t("configure_sso_setting.sso_config.metadata_url_required");
            } else if (!urlPattern.test(formState.metadataUrl)) {
                newErrors.metadataUrl = t("configure_sso_setting.sso_config.metadata_invalid_url");
            }
        }

        // Validate file if the selection is 'MetadataCertificate'
        if (formState.metadataSelection === MetadataChoice.MetadataCertificate) {
            if (!formState.metadataCertificate.file) {
                newErrors.file = t("configure_sso_setting.sso_config.metadata_file_required");
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (isReadOnlyCompanyUser) {
            return;
        }

        setSubmitDisabled(true);
        if (validateForm()) {
            try {
                const mutationVariables: {
                    metadataSelection: MetadataChoice;
                    samlAttributeEmail: string;
                    providerType: string;
                    [key: string]: string;
                } = {
                    metadataSelection: formState.metadataSelection as MetadataChoice,
                    samlAttributeEmail: formState.samlAttribute,
                    providerType: "SAML",
                };

                // If metadataSelection is MetadataCertificate and file is provided, convert the file to Base64
                if (
                    formState.metadataSelection === MetadataChoice.MetadataCertificate &&
                    formState.metadataCertificate.file
                ) {
                    const base64File = await fileToBase64(formState.metadataCertificate.file);
                    mutationVariables[MetadataChoice.MetadataCertificate] = base64File;
                } else if (formState.metadataSelection === MetadataChoice.MetadataUrl) {
                    mutationVariables.metadataDocumentEndpointURL = formState.metadataUrl;
                }

                // Execute the mutation with the prepared variables
                await mutate({ variables: mutationVariables });

                setSavedState({
                    samlAttribute: formState.samlAttribute,
                    metadataUrl: formState.metadataUrl,
                    metadataCertificate: {
                        file: formState.metadataCertificate.file,
                        name: formState.metadataCertificate.name,
                    },
                    metadataSelection: formState.metadataSelection,
                });

                if (formState.metadataSelection === MetadataChoice.MetadataCertificate) {
                    // Clear the file input after submission
                    if (fileInputRef.current) {
                        fileInputRef.current.value = "";
                    }
                    // Reset the file state, keep the name as that would be displayed in the UI
                    setFormState((prevFormState) => ({
                        ...prevFormState,
                        metadataCertificate: { ...prevFormState.metadataCertificate, file: null },
                    }));
                }
            } catch (error) {
                Notifier.fail(t("configure_sso_setting.sso_error_msg"));
            }
        }
    };

    return (
        <SSOContainer>
            <SSOWrapper>
                <h3>
                    <HeadingContainer>
                        {t("configure_sso_setting.enable_sso")} &nbsp;&nbsp;
                        <Radio
                            data-test-id={`qa_setting.enable_sso`}
                            data-testid="qa_setting.enable_sso"
                            toggle
                            checked={isEditable}
                            disabled={isReadOnlyCompanyUser}
                            onChange={handleToggleChange}
                        />
                    </HeadingContainer>
                </h3>
                <VersionInfoContainer>
                    <StyledInfoIcon name="info circle" size="large" />
                    <VersionText>
                        {Parser(t("configure_sso_setting.sso_supported_version"))}
                    </VersionText>
                </VersionInfoContainer>
                <GrabcadDetails
                    authSettings={props.authSettings}
                    isEditable={isEditable && !isReadOnlyCompanyUser}
                />
                <Form onSubmit={handleSubmit}>
                    <Row>
                        <ColumnA color="white">
                            <h4>{t("configure_sso_setting.sso_configuration")}</h4>
                            <FormField>
                                <StyledLabelWrapper>
                                    <StyledLabel>
                                        {t(
                                            "configure_sso_setting.sso_config.saml_attribute_mapping_label"
                                        )}
                                    </StyledLabel>
                                    <StyledSup>
                                        <Popup
                                            trigger={
                                                <span>
                                                    <StyledInfoIcon
                                                        name="info circle"
                                                        size="large"
                                                    />
                                                </span>
                                            }
                                            content={t(
                                                "configure_sso_setting.sso_config.saml_attribute_mapping_info"
                                            )}
                                            position="right center"
                                        />
                                    </StyledSup>
                                </StyledLabelWrapper>
                                <input
                                    type="text"
                                    data-test-id={`qa_saml_attribute_mapping`}
                                    placeholder={t(
                                        "configure_sso_setting.sso_config.saml_attribute_mapping_placeholder"
                                    )}
                                    value={isReadOnlyCompanyUser ? "" : formState.samlAttribute}
                                    disabled={!isEditable || isReadOnlyCompanyUser}
                                    onChange={(e) => handleChange(e, "samlAttribute")}
                                />
                                {errors.samlAttribute && (
                                    <ErrorMessage>{errors.samlAttribute}</ErrorMessage>
                                )}
                            </FormField>
                            <FormField>
                                <StyledLabelWrapper>
                                    <StyledLabel>
                                        {t(
                                            "configure_sso_setting.sso_config.metadata_document_source"
                                        )}
                                    </StyledLabel>
                                    <StyledSup>
                                        <Popup
                                            trigger={
                                                <span>
                                                    <StyledInfoIcon
                                                        name="info circle"
                                                        size="large"
                                                    />
                                                </span>
                                            }
                                            content={t(
                                                "configure_sso_setting.sso_config.metadata_account_endpoint_url_info"
                                            )}
                                            position="right center"
                                        />
                                    </StyledSup>
                                </StyledLabelWrapper>
                                <div>
                                    <RadioGroupContainer>
                                        <RadioContainer>
                                            <Radio
                                                label={t(
                                                    "configure_sso_setting.sso_config.metadata_url"
                                                )}
                                                disabled={!isEditable || isReadOnlyCompanyUser}
                                                name="radioGroup"
                                                value={MetadataChoice.MetadataUrl}
                                                checked={
                                                    displayedMetadataSelection ===
                                                    MetadataChoice.MetadataUrl
                                                }
                                                onChange={handleMetadataSelection}
                                                style={{ marginRight: "10px" }}
                                                data-testid="radio-btn-metadata_url"
                                            />
                                        </RadioContainer>
                                        <RadioContainer>
                                            <Radio
                                                label={t(
                                                    "configure_sso_setting.sso_config.metadata_xml"
                                                )}
                                                disabled={!isEditable || isReadOnlyCompanyUser}
                                                name="radioGroup"
                                                value={MetadataChoice.MetadataCertificate}
                                                checked={
                                                    displayedMetadataSelection ===
                                                    MetadataChoice.MetadataCertificate
                                                }
                                                onChange={handleMetadataSelection}
                                                data-testid="radio-btn-metadata_xml"
                                            />
                                        </RadioContainer>
                                    </RadioGroupContainer>
                                    {showModal && (
                                        <UploadMetadataInfoModal
                                            open={showModal}
                                            setOpen={setShowModal}
                                            onConfirm={() => {
                                                if (pendingMetadataChoice) {
                                                    setFormState((prevFormState) => ({
                                                        ...prevFormState,
                                                        metadataSelection: pendingMetadataChoice,
                                                    }));
                                                    setErrors({});
                                                    setPendingMetadataChoice(null);
                                                }
                                            }}
                                            onCancel={() => {
                                                setFormState((prevFormState) => ({
                                                    ...prevFormState,
                                                    metadataSelection: MetadataChoice.MetadataUrl,
                                                }));
                                                setErrors({});
                                                setPendingMetadataChoice(null);
                                            }}
                                        />
                                    )}
                                    {/* File or Text Input Section */}
                                    <InputContainer>
                                        {displayedMetadataSelection ===
                                        MetadataChoice.MetadataCertificate ? (
                                            <FormField>
                                                <FileInput
                                                    type="file"
                                                    onChange={handleFileChange}
                                                    id="file-upload-input"
                                                    disabled={!isEditable || isReadOnlyCompanyUser}
                                                    ref={fileInputRef}
                                                    accept=".xml,application/xml,text/xml"
                                                />
                                                <FileLabel htmlFor="file-upload-input">
                                                    {!isReadOnlyCompanyUser &&
                                                    !!formState.metadataCertificate.name ? (
                                                        <>
                                                            {formState.metadataCertificate.name}{" "}
                                                            <Icon
                                                                name="close"
                                                                size="small"
                                                                style={{
                                                                    cursor: "pointer",
                                                                    marginLeft: "8px",
                                                                }}
                                                                disabled={
                                                                    submitDisabled ||
                                                                    isReadOnlyCompanyUser
                                                                }
                                                                onClick={(
                                                                    e: React.MouseEvent<HTMLDivElement>
                                                                ) => {
                                                                    if (isReadOnlyCompanyUser) {
                                                                        return;
                                                                    }
                                                                    e.preventDefault();
                                                                    setSubmitDisabled(true);
                                                                    setFormState(
                                                                        (prevFormState) => ({
                                                                            ...prevFormState,
                                                                            metadataCertificate: {
                                                                                ...prevFormState.metadataCertificate,
                                                                                name: null,
                                                                            },
                                                                        })
                                                                    );
                                                                }}
                                                                data-test-id={`qa_file_remove_icon`}
                                                            />
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Icon
                                                                name="folder open"
                                                                size="small"
                                                                style={{
                                                                    cursor: "pointer",
                                                                    marginRight: "8px",
                                                                }}
                                                            />
                                                            {t(
                                                                "configure_sso_setting.sso_config.metadata_certificate_placeholder"
                                                            )}
                                                        </>
                                                    )}
                                                </FileLabel>
                                                {errors.file && (
                                                    <Message negative content={errors.file} />
                                                )}
                                            </FormField>
                                        ) : (
                                            <FormField>
                                                <input
                                                    type="text"
                                                    data-test-id={`qa_metadata_account_endpoint`}
                                                    placeholder={t(
                                                        "configure_sso_setting.sso_config.metadata_account_endpoint_url_placeholder"
                                                    )}
                                                    value={
                                                        isReadOnlyCompanyUser
                                                            ? ""
                                                            : formState.metadataUrl
                                                    }
                                                    disabled={!isEditable || isReadOnlyCompanyUser}
                                                    onChange={(e) => handleChange(e, "metadataUrl")}
                                                    style={{ width: "100%" }}
                                                />
                                                {errors.metadataUrl && (
                                                    <ErrorMessage>
                                                        {errors.metadataUrl}
                                                    </ErrorMessage>
                                                )}
                                            </FormField>
                                        )}
                                    </InputContainer>
                                </div>
                            </FormField>

                            <div style={{ float: "right" }}>
                                <Button
                                    type="button"
                                    disabled={
                                        isReadOnlyCompanyUser ||
                                        !isDirty ||
                                        (!isFormValid && submitDisabled) ||
                                        loading
                                    }
                                    data-tip={!isFormValid ? "Please fill all required fields" : ""}
                                    onClick={() => setShowConfirmation(true)} // <-- open modal on click
                                >
                                    {loading
                                        ? "Saving..."
                                        : t("configure_sso_setting.sso_save_btn")}
                                </Button>
                                <SsoConfirmationModal
                                    open={showConfirmation}
                                    setOpen={setShowConfirmation}
                                    onCancel={() => setShowConfirmation(false)}
                                    onConfirm={async () => {
                                        setShowConfirmation(false);
                                        await handleSubmit(
                                            new Event("submit") as unknown as React.FormEvent
                                        );
                                    }}
                                />
                            </div>
                        </ColumnA>
                        <ColumnB color="#F5F5F5">
                            <h4>
                                <Icon
                                    size="large"
                                    name="info circle"
                                    style={{ color: "#4183c4" }}
                                />
                                &nbsp;&nbsp;
                                {t("configure_sso_setting.instructions")}
                            </h4>
                            <InlineTable>
                                <tbody>
                                    <tr>
                                        <InlineTableCell>
                                            <img src={One} />
                                        </InlineTableCell>
                                        <td
                                            style={{
                                                verticalAlign: "middle",
                                                paddingBottom: "1.5rem",
                                            }}
                                        >
                                            <span>
                                                {" "}
                                                {t("configure_sso_setting.sso_instructions.inst_1")}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <InlineTableCell>
                                            <img src={Two} />
                                        </InlineTableCell>
                                        <td
                                            style={{
                                                verticalAlign: "middle",
                                                paddingBottom: "1.5rem",
                                            }}
                                        >
                                            <span>
                                                {" "}
                                                {t("configure_sso_setting.sso_instructions.inst_2")}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <InlineTableCell>
                                            <img src={Three} />
                                        </InlineTableCell>
                                        <td
                                            style={{
                                                verticalAlign: "middle",
                                                paddingBottom: "1.5rem",
                                            }}
                                        >
                                            <span>
                                                {" "}
                                                {t("configure_sso_setting.sso_instructions.inst_3")}
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </InlineTable>
                        </ColumnB>
                    </Row>
                </Form>
            </SSOWrapper>
        </SSOContainer>
    );
};

export default ConfigureSSO;
